import React from 'react';
import { locationInfo } from '../mock';
import { MapPin, Clock, Phone, Mail } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const Location = () => {
  return (
    <section id="location" className="py-24 px-6 bg-black relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Find <span className="text-cyan-400">Us</span>
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300">
            Launch pad coordinates for your next flavor mission
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Location Card */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 hover:border-orange-500/50 transition-all duration-300 group">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-7 h-7 text-white" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Location</h3>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    {locationInfo.address}
                  </p>
                  {/* Get Directions Button */}
                  <Button
                    asChild
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                  >
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Surprise+AZ" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <MapPin className="w-4 h-4" />
                      Get Directions
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-cyan-400 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-7 h-7 text-black" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">Hours</h3>
                  <div className="space-y-2">
                    {locationInfo.hours.map((schedule, index) => (
                      <div key={index} className="flex justify-between gap-4">
                        <span className="text-gray-400 font-medium text-lg">{schedule.day}</span>
                        <span className="text-cyan-400 font-semibold text-lg">{schedule.time}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-orange-500 font-semibold text-sm mt-3">{locationInfo.established}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Card */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 hover:border-cyan-400/50 transition-all duration-300 group">
            <CardContent className="p-8 h-full flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-7 h-7 text-white" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Call/Text</h3>
                  <a href={`tel:${locationInfo.phone}`} className="text-gray-300 text-lg hover:text-orange-500 transition-colors">
                    {locationInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-cyan-400 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-7 h-7 text-black" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Email</h3>
                  <a href={`mailto:${locationInfo.email}`} className="text-gray-300 text-lg hover:text-cyan-400 transition-colors">
                    {locationInfo.email}
                  </a>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-orange-500/10 to-cyan-400/10 rounded-xl border border-orange-500/20">
                <p className="text-gray-300 text-center">
                  <span className="text-orange-500 font-semibold text-lg">Delivery Available!</span>
                  <br />
                  Call us for delivery orders
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Google Maps Embed */}
        <div className="mt-8">
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 overflow-hidden">
            <CardContent className="p-0">
              <div className="relative w-full h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d106309.84959654197!2d-112.46850904999999!3d33.6303595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b6c39e8a2ba05%3A0xb8ab7cdae3f14b78!2sSurprise%2C%20AZ!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sauce Dogz Location - Surprise, AZ"
                  className="rounded-lg"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Location;